import type { PayerResponseReason } from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { formatRemittanceCurrency } from '../remittance/remittanceFormatters';

/**
 * Display strings for one payer reason (EMR-1241). These live outside the cell,
 * the list and the drawer because all three render the same reason and the
 * Billing Manager and the NOA Manager must not each carry a copy of the rule.
 *
 * The reason data itself comes from the shared read model (EMR-1240), which
 * already merged four evidence sources into one `source` field.
 */

const SOURCE_LABELS: Record<PayerResponseReason['source'], string> = {
  // Stedi refused the claim before the payer ever saw it.
  stedi: 'Clearinghouse',
  payer_277: 'Payer',
  payer_835: 'Payer',
};

export const sourceLabel = (source: string): string =>
  SOURCE_LABELS[source as PayerResponseReason['source']] ?? 'Unknown';

export interface FormattedReason {
  text: string;
  /** A muted second line, or null when the reason needs no apology. */
  hint: string | null;
}

/**
 * The unknown-code ladder: use the first rung the data supports, so the biller
 * always gets an actionable line rather than a blank cell.
 */
export const formatReason = (
  reason: Pick<PayerResponseReason, 'code' | 'description'>,
  payerClaimNumber?: string | null,
  kind: 'claim' | 'noa' = 'claim'
): FormattedReason => {
  const noun = kind === 'noa' ? 'NOA' : 'claim';
  const code = reason.code?.trim();
  const description = reason.description?.trim();

  if (code && description)
    return { text: `${code} — ${description}`, hint: null };
  if (description) return { text: description, hint: null };
  if (code) {
    return { text: code, hint: 'The payer did not include a description.' };
  }
  return {
    text: payerClaimNumber
      ? `The payer rejected this ${noun} but did not say why. Call the payer with claim # ${payerClaimNumber}.`
      : `The payer rejected this ${noun} but did not say why. Call the payer.`,
    hint: null,
  };
};

type ReasonServiceLine = NonNullable<PayerResponseReason['service_line']>;

/**
 * Names an 835 service line by the payer's own adjudicated values (EMR-2593).
 * Roger sends no line item control number, so these are the only identifiers
 * the remittance gives; an absent one is left out rather than invented. The
 * remittance it came from is named too: a claim that was paid, reversed and
 * corrected has the same line on each remittance.
 */
export const serviceLineLabel = (line: ReasonServiceLine): string => {
  const start = line.service_date
    ? formatDateToMMDDYYYY(line.service_date)
    : null;
  // A range is shown only when the end is a different day than the start.
  const end =
    line.service_end_date && line.service_end_date !== line.service_date
      ? formatDateToMMDDYYYY(line.service_end_date)
      : null;
  return [
    'Service line',
    start && end ? `${start} – ${end}` : start,
    line.revenue_code ? `Rev ${line.revenue_code}` : null,
    line.procedure_code || null,
    // Test the data, not the formatter's placeholder output.
    line.charge_amount
      ? `${formatRemittanceCurrency(line.charge_amount)} charge`
      : null,
    line.remittance_received_on
      ? `remittance ${formatDateToMMDDYYYY(line.remittance_received_on)}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

export interface ServiceLineReasonGroup<T> {
  line: ReasonServiceLine;
  reasons: T[];
}

export interface GroupedReasons<T> {
  /** Reasons the payer gave for the claim as a whole, in their given order. */
  claimLevel: T[];
  /** Reasons the 835 reported inside a service line, one group per line. */
  lines: ServiceLineReasonGroup<T>[];
}

/**
 * Splits reasons by the service line the 835 reported them on (EMR-2593). A
 * reason with no line stays claim-level: nothing is assigned to a line here.
 * Lines are ordered by service date, revenue code, procedure code, remittance
 * date and charge, then by id, so the order is total: it does not depend on the
 * order the reasons arrived in, and does not reshuffle between reads.
 */
export const groupReasonsByServiceLine = <
  T extends Pick<PayerResponseReason, 'service_line'>,
>(
  reasons: T[]
): GroupedReasons<T> => {
  const claimLevel: T[] = [];
  const byLineId = new Map<string, ServiceLineReasonGroup<T>>();
  for (const reason of reasons) {
    const line = reason.service_line;
    if (!line) {
      claimLevel.push(reason);
      continue;
    }
    const group = byLineId.get(line.id);
    if (group) group.reasons.push(reason);
    else byLineId.set(line.id, { line, reasons: [reason] });
  }
  const sortKey = ({ line }: ServiceLineReasonGroup<T>) =>
    [
      // An undated line sorts after every dated one.
      line.service_date ?? '9999-99-99',
      line.revenue_code ?? '',
      line.procedure_code ?? '',
      line.remittance_received_on ?? '9999-99-99',
      line.charge_amount ?? '',
      // Ids are unique, so no two lines tie.
      line.id,
    ].join('|');
  const lines = [...byLineId.values()].sort((a, b) =>
    sortKey(a).localeCompare(sortKey(b))
  );
  return { claimLevel, lines };
};
